import React from "react";
import { Box, Chip, Stack, Typography } from "@mui/material";
import { Task } from "model/activitiesModel";
import MobileTaskCardCompact from "./MobileTaskCardCompact";
import EmptyMobileTasks from "./EmptyMobileTasks";
import { getTranslation } from "common";

interface MobileSplitLayoutProps {
  todoTasks: Task[];
  doingTasks: Task[];
  onTaskClick: (idprocesstoken: number) => void;
  onNextStep?: (task: Task) => void | Promise<void>;
  onBackStep?: (task: Task) => void | Promise<void>;
  todoEmptyMessage: string;
  doingEmptyMessage: string;
  translations: Record<string, string>;
}

interface SplitColumnProps {
  title: string;
  tasks: Task[];
  accentColor: string;
  emptyMessage: string;
  onTaskClick: (idprocesstoken: number) => void;
  onNextStep?: (task: Task) => void | Promise<void>;
  onBackStep?: (task: Task) => void | Promise<void>;
}

/**
 * Mobile landscape split layout: two columns side-by-side (Da Fare | In Corso).
 * No tabs in landscape — columns are always visible at the same time.
 */
const MobileSplitLayout: React.FC<MobileSplitLayoutProps> = ({
  todoTasks,
  doingTasks,
  onTaskClick,
  onNextStep,
  onBackStep,
  todoEmptyMessage,
  doingEmptyMessage,
  translations,
}) => {
  return (
    <Box
      sx={{
        flex: 1,
        display: "flex",
        gap: 1.5,
        p: 1.5,
        bgcolor: (t) => (t.palette.mode === "dark" ? "background.default" : "grey.100"),
        overflow: "hidden",
      }}
    >
      <SplitColumn
        title={getTranslation(translations, "pm.label.process_status.todo")}
        tasks={todoTasks}
        accentColor="#54bdc1"
        emptyMessage={todoEmptyMessage}
        onTaskClick={onTaskClick}
        onNextStep={onNextStep}
        onBackStep={onBackStep}
      />
      <SplitColumn
        title={getTranslation(translations, "pm.label.process_status.doing")}
        tasks={doingTasks}
        accentColor="#ed6c02"
        emptyMessage={doingEmptyMessage}
        onTaskClick={onTaskClick}
        onNextStep={onNextStep}
        onBackStep={onBackStep}
      />
    </Box>
  );
};

const SplitColumn: React.FC<SplitColumnProps> = ({
  title,
  tasks,
  accentColor,
  emptyMessage,
  onTaskClick,
  onNextStep,
  onBackStep,
}) => (
  <Box
    sx={{
      flex: 1,
      minWidth: 0,
      bgcolor: "background.paper",
      border: "1px solid",
      borderColor: "divider",
      borderRadius: 2,
      display: "flex",
      flexDirection: "column",
      overflow: "hidden",
      boxShadow: 0.5,
    }}
  >
    <Stack
      direction="row"
      alignItems="center"
      justifyContent="space-between"
      sx={{
        px: 1.25,
        py: 1,
        borderBottom: "1px solid",
        borderColor: "divider",
      }}
    >
      <Stack direction="row" alignItems="center" spacing={0.75}>
        <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: accentColor }} />
        <Typography
          variant="caption"
          sx={{
            fontSize: 10,
            fontWeight: 700,
            color: "text.primary",
            textTransform: "uppercase",
            letterSpacing: 0.6,
          }}
        >
          {title}
        </Typography>
      </Stack>
      <Chip
        label={tasks.length}
        size="small"
        sx={{
          height: 16,
          fontSize: 9,
          fontWeight: 700,
          bgcolor: (t) => (t.palette.mode === "dark" ? "action.hover" : "grey.200"),
          color: "text.secondary",
          "& .MuiChip-label": { px: 0.75 },
        }}
      />
    </Stack>
    <Box
      sx={{
        flex: 1,
        overflowY: "auto",
        p: 1,
        "&::-webkit-scrollbar": { display: "none" },
        scrollbarWidth: "none",
      }}
    >
      {tasks.length === 0 ? (
        <EmptyMobileTasks message={emptyMessage} accentColor={accentColor} />
      ) : (
        <Stack spacing={1}>
          {tasks.map((task) => (
            <MobileTaskCardCompact
              key={task.idprocesstoken}
              task={task}
              onClick={() => onTaskClick(task.idprocesstoken)}
              onNext={onNextStep}
              onBack={onBackStep}
            />
          ))}
        </Stack>
      )}
    </Box>
  </Box>
);

export default MobileSplitLayout;